import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Loader2, RefreshCw, Database, Clock, Radio, Search } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Owner-only DLD data refresh panel. Rendered on /admin dashboard for
 * `role === "admin"` users only. Advisors never see this section.
 *
 * Behaviour:
 *   - On mount: polls /api/admin/dld/status every 6s so the "latest week"
 *     card stays fresh even when the Monday cron just fired.
 *   - "Force fetch now" → POST /api/admin/dld/force-refresh, then bumps poll
 *     to every 3s while `manual.running` is true. Toasts on success/error.
 */
export default function DLDRefreshPanel() {
  const [data, setData] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [retryingGsc, setRetryingGsc] = useState(false);
  const [rebuildingSitemap, setRebuildingSitemap] = useState(false);
  const prevRunning = useRef(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/dld/status");
      setData(r.data);
      const runningNow = !!r.data?.manual?.running;
      // Detect the running -> not-running transition so we surface a toast
      // once the manual refresh finishes.
      if (prevRunning.current && !runningNow) {
        const err = r.data?.manual?.last_error;
        const res = r.data?.manual?.last_result;
        if (err) {
          toast.error(`DLD fetch failed: ${err}`, { duration: 8000 });
        } else if (res) {
          toast.success("DLD data fetched. Latest week refreshed on /insights.");
        }
      }
      prevRunning.current = runningNow;
    } catch {
      // silent (non-owner tokens see 403; component is guarded upstream)
    }
  }, []);

  useEffect(() => {
    load();
    // Fast poll while running, slow poll otherwise.
    const tick = () => load();
    const interval = data?.manual?.running ? 3000 : 6000;
    pollRef.current = setInterval(tick, interval);
    return () => clearInterval(pollRef.current);
  }, [load, data?.manual?.running]);

  const rebuildSitemap = async () => {
    setRebuildingSitemap(true);
    try {
      const r = await api.post("/admin/sitemap/rebuild");
      const smMeta = r.data?.sitemap || {};
      const idn = r.data?.indexnow || {};
      const gsc = r.data?.gsc || {};
      if (smMeta.error) {
        toast.error(`Sitemap rebuild failed: ${smMeta.error}`, { duration: 8000 });
      } else {
        const parts = [
          `sitemap OK · ${smMeta.total_urls || "?"} URLs · latest ${smMeta.latest_week || "—"}`,
          idn.ok !== false ? `IndexNow HTTP ${idn.status_code || "?"}` : "IndexNow failed",
          gsc.ok ? `GSC HTTP ${gsc.status_code || "?"}` : (gsc.error ? "GSC failed" : "GSC skipped"),
        ];
        toast.success(parts.join(" · "), { duration: 8000 });
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not rebuild the sitemap.");
    } finally {
      setRebuildingSitemap(false);
    }
  };

  const trigger = async () => {
    setTriggering(true);
    try {
      const r = await api.post("/admin/dld/force-refresh");
      if (r.data?.already_running) {
        toast.info("A fetch is already running. Watch this panel for results.");
      } else {
        toast.success("Force fetch queued. Poll running…");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not queue the DLD fetch.");
    } finally {
      setTriggering(false);
    }
  };

  const retryGsc = async () => {
    setRetryingGsc(true);
    try {
      const r = await api.post("/admin/gsc/resubmit");
      if (r.data?.ok) {
        toast.success(`Google GSC accepted the sitemap (HTTP ${r.data.status_code}).`);
      } else {
        toast.error(`Google GSC still failing: ${r.data?.error || "unknown"}`);
      }
      await load();    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reach Google Search Console.");
    } finally {
      setRetryingGsc(false);
    }
  };

  const running = !!data?.manual?.running;
  const fetch = data?.fetch || {};
  const agg = data?.aggregate || {};
  const latest = data?.latest_week;
  const lastErr = data?.manual?.last_error;

  // Derive a status pill: green when both fetch+agg are done AND no error,
  // amber while running, red on any error.
  let statusTone = "gold";
  let statusText = "Idle";
  let StatusIcon = Database;
  if (running || fetch.state === "running" || agg.state === "running") {
    statusTone = "amber"; statusText = "Fetching…"; StatusIcon = Loader2;
  } else if (lastErr || fetch.state === "error" || agg.state === "error") {
    statusTone = "red"; statusText = "Last run: error"; StatusIcon = AlertTriangle;
  } else if (latest?.iso_week) {
    statusTone = "green"; statusText = `Last run: OK · ${latest.iso_week}`; StatusIcon = CheckCircle2;
  }

  const toneClasses = {
    gold: "text-propertic-gold border-propertic-gold/40 bg-propertic-sand/50",
    amber: "text-amber-800 border-amber-400/60 bg-amber-50",
    green: "text-emerald-800 border-emerald-400/50 bg-emerald-50",
    red: "text-red-800 border-red-400/60 bg-red-50",
  }[statusTone];

  return (
    <section
      data-testid="dld-refresh-panel"
      className="mt-10 bg-white rounded-2xl border hairline p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <p className="font-mono-brand text-propertic-gold mb-1 text-[10px] tracking-[0.16em] uppercase">
            Owner console · DLD data pipeline
          </p>
          <h2 className="font-editorial text-2xl text-propertic-navy">
            Weekly DLD refresh
          </h2>
          <p className="text-[12.5px] text-propertic-muted mt-1 leading-relaxed max-w-2xl">
            The Monday cron pulls the last 14 days from the DLD Open Data Gateway at
            <span className="text-propertic-navy font-medium"> 11:00 UAE local time (07:00 UTC)</span>,
            re-aggregates weekly + monthly rollups, and rebuilds Roni's `roni_data` slice.
            Use "Force fetch now" if you need to retry outside the schedule.
          </p>
        </div>
        <div
          data-testid="dld-refresh-status-pill"
          className={`inline-flex items-center gap-2 h-9 px-3 rounded-full border text-[11px] font-mono-brand tracking-[0.12em] uppercase ${toneClasses}`}
        >
          <StatusIcon className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} />
          {statusText}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
        <StatCell
          label="Latest ISO week"
          value={latest?.iso_week || "—"}
          hint={latest?.end_date ? `ends ${latest.end_date}` : "no weekly doc"}
          testid="dld-stat-latest-week"
        />
        <StatCell
          label="Last cron heartbeat"
          value={fmtShort(fetch.finished_at || fetch.started_at)}
          hint={fetch.state || "unknown"}
          testid="dld-stat-cron"
        />
        <StatCell
          label="Aggregate state"
          value={agg.state || "—"}
          hint={fmtShort(agg.finished_at || agg.started_at)}
          testid="dld-stat-agg"
        />
        <StatCell
          label="Sitemap"
          value={data?.sitemap?.total_urls ? `${data.sitemap.total_urls} URLs` : "—"}
          hint={
            data?.sitemap?.written_at
              ? `${fmtShort(data.sitemap.written_at)} · latest ${data.sitemap.latest_week || "—"}`
              : "not generated yet"
          }
          testid="dld-stat-sitemap"
        />
        <StatCell
          label="IndexNow ping"
          value={data?.indexnow?.status_code ? `HTTP ${data.indexnow.status_code}` : "—"}
          hint={
            data?.indexnow?.pinged_at
              ? `${fmtShort(data.indexnow.pinged_at)} · ${data.indexnow.submitted_count || 0} URLs`
              : "never (auto-fires after Monday refresh)"
          }
          testid="dld-stat-indexnow"
        />
        <StatCell
          label="Google GSC"
          value={
            data?.gsc?.ok === true
              ? `HTTP ${data.gsc.status_code}`
              : data?.gsc?.status_code
                ? `HTTP ${data.gsc.status_code}`
                : data?.gsc?.error
                  ? "error"
                  : "—"
          }
          hint={
            data?.gsc?.submitted_at
              ? `${fmtShort(data.gsc.submitted_at)}${data.gsc.ok ? " · sitemap accepted" : " · needs retry"}`
              : "not submitted yet (key required)"
          }
          testid="dld-stat-gsc"
          tone={data?.gsc?.ok ? "green" : data?.gsc?.error ? "red" : "neutral"}
        />
      </div>

      {data?.indexnow?.weekly_count > 0 && (
        <div
          data-testid="dld-indexnow-detail"
          className="mb-5 rounded-xl border hairline bg-propertic-sand/40 px-4 py-3 flex items-start gap-2 text-[12px] text-propertic-navy/85"
        >
          <Radio className="w-3.5 h-3.5 mt-0.5 shrink-0 text-propertic-gold" />
          <div>
            <p className="font-medium">
              Notified Bing · Yandex · Naver · Seznam · Yep
            </p>
            <p className="text-propertic-muted mt-0.5">
              {data.indexnow.static_count || 0} static pages + {data.indexnow.weekly_count} weekly recaps ·
              last submitted week: <span className="font-mono">{(data.indexnow.weekly_urls?.[0] || "").split("/").pop() || "—"}</span>
            </p>
          </div>
        </div>
      )}

      {data?.gsc && (data.gsc.error || data.gsc.ok === false) && (
        <div
          data-testid="dld-gsc-error"
          className="mb-5 rounded-xl border border-red-300 bg-red-50 text-red-800 text-[12.5px] px-4 py-3 flex items-start gap-3 flex-wrap"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="font-medium mb-0.5">
              Google Search Console submission failed
              {data.gsc.status_code ? ` (HTTP ${data.gsc.status_code})` : ""}
            </p>
            <p className="font-mono text-[11px] break-all">{data.gsc.error || "unknown error"}</p>
            {data.gsc.hint && (
              <p className="mt-1 text-[11.5px] text-red-900/85 not-italic">{data.gsc.hint}</p>
            )}
          </div>
          <button
            onClick={retryGsc}
            disabled={retryingGsc}
            data-testid="dld-gsc-retry-btn"
            className="rounded-full h-9 px-4 border border-red-400 text-red-800 hover:bg-red-100 text-[11px] font-mono-brand tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
          >
            {retryingGsc ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> Retrying…</>
            ) : (
              <><Search className="w-3 h-3" /> Retry Google</>
            )}
          </button>
        </div>
      )}

      {lastErr && (
        <div
          data-testid="dld-refresh-error"
          className="mb-5 rounded-xl border border-red-300 bg-red-50 text-red-800 text-[12.5px] px-4 py-3 flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium mb-0.5">Last manual fetch failed</p>
            <p className="font-mono text-[11px] break-all">{lastErr}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={trigger}
          disabled={triggering || running}
          data-testid="dld-force-refresh-btn"
          className="btn-gold rounded-full h-11 px-5 text-xs font-mono-brand tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
        >
          {running || triggering ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching…</>
          ) : (
            <><RefreshCw className="w-3.5 h-3.5" /> Force fetch now</>
          )}
        </button>
        <button
          onClick={rebuildSitemap}
          disabled={rebuildingSitemap}
          data-testid="dld-sitemap-rebuild-btn"
          className="rounded-full h-11 px-5 text-xs font-mono-brand tracking-widest inline-flex items-center gap-2 border border-propertic-navy/30 text-propertic-navy hover:bg-propertic-cream disabled:opacity-60"
          title="Regenerate sitemap.xml on disk, push to IndexNow (Bing/Yandex/Naver/Seznam/Yep) and Google Search Console. The dynamic /api/sitemap.xml endpoint is always fresh regardless."
        >
          {rebuildingSitemap ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rebuilding…</>
          ) : (
            <><Radio className="w-3.5 h-3.5" /> Rebuild sitemap &amp; ping</>
          )}
        </button>
        <p className="text-[11px] text-propertic-muted inline-flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Next auto-run: Monday 11:00 UAE
        </p>
      </div>
    </section>
  );
}

function StatCell({ label, value, hint, testid, tone }) {
  const toneBorder = tone === "green"
    ? "border-emerald-300/70 bg-emerald-50/60"
    : tone === "red"
      ? "border-red-300/70 bg-red-50/60"
      : "hairline";
  return (
    <div className={`border rounded-xl p-3.5 ${toneBorder}`} data-testid={testid}>
      <p className="font-mono-brand text-propertic-muted text-[9px] tracking-[0.16em] uppercase mb-1">{label}</p>
      <p className="font-editorial text-lg text-propertic-navy tabular-nums break-all">{value}</p>
      {hint && <p className="mt-1 text-[10.5px] text-propertic-muted">{hint}</p>}
    </div>
  );
}

function fmtShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
